// Core React imports
import React, { useState, useContext } from 'react';

// Shared UI components from T-Mobile design system
import {
  TmoButtons as TmoButton, // Primary/secondary CTA buttons
  ConfirmationModal, // Reusable confirmation dialog
  DynamicList // Table/list renderer for dynamic data
} from '@tmobile/tmo-bps-syncup-web-component-lib';

// Utility to trigger file downloads in browser
import FileSaver from 'file-saver';

// Custom hook for manually-triggered async actions
import useManualFetcher from '../hooks/useManualFetcher';

// Service layer for device-related backend APIs
import deviceService from '../services/deviceService';

// CSV file upload component
import FileUpload from './fileUpload/FileUpload';

// Utility helpers
import { arrayBufferToString, pluralize } from '../utils/stringUtils';
import { getCurrentDateTimeString } from '../utils/dateUtils';

// Global status/toast context
import StatusContext from '../context/StatusContext';

// Fetch lifecycle constants
import { FETCH_STATE } from '../utils/app_constants';

// Application Insights / analytics tracking
import { trackCustomEvents } from '../utils/appInsight_analytics';

// Component-specific styles
import './Unclaim.module.css';

/**
 * Column mapping for the initial staged list
 * (before the unclaim operation is executed)
 */
const headerNameListItemMap = [
  {
    listItemKey: 'hardwareId',
    headerDisplayName: 'IMEI'
  },
  {
    listItemKey: 'status'
  },
  {
    listItemKey: 'message'
  }
];

/**
 * Column mapping for the final result list
 * (after unclaim operation completes)
 */
const resultHeaderNameListItemMap = [
  {
    listItemKey: 'hardwareId',
    headerDisplayName: 'IMEI'
  },
  {
    listItemKey: 'status'
  },
  {
    listItemKey: 'message'
  },
  {
    listItemKey: 'product'
  }
];

/**
 * Unclaim
 *
 * Handles bulk unclaiming (unregistering) of devices using a CSV upload.
 * Flow:
 *  1. Upload CSV containing IMEIs
 *  2. Stage IMEIs for review
 *  3. Confirm bulk unclaim
 *  4. Execute API call
 *  5. Display results and allow CSV export
 */
const Unclaim = () => {
  // Controls visibility of confirmation modal
  const [isModalOpen, setIsModalOpen] = useState(false);

  // Determines whether to show staged list or final results list
  const [showResultTable, setShowResultTable] = useState(false);

  // Holds IMEI list in both staged and processed form
  const [hardwareIds, setHardwareIds] = useState();

  // Global success/error notification handlers
  const { addSuccessMessage, addErrorMessage } = useContext(StatusContext);

  /**
   * Manual fetcher hook for bulk unclaim operation.
   * Handles API invocation, analytics, and UI lifecycle.
   */
  const [fetchState, actionTrigger, error, resetFetchState] = useManualFetcher({
    action: () => {
      // Track analytics event before API call
      trackCustomEvents('Bulk Unclaim Devices', {
        hardwareIds: hardwareIds.map(i => i.hardwareId)
      });

      // Backend API call to unregister devices in bulk
      return deviceService.bulkUnregisterDevice({
        hardwareIds: hardwareIds.map(i => i.hardwareId)
      });
    },

    // On successful API response
    onLoad: data => {
      addSuccessMessage({
        message: 'Bulk unclaim completed successfully'
      });

      // Replace staged list with processed result list
      setHardwareIds(data);
    },

    // On API failure
    onError: error => {
      addErrorMessage({ error });
    },

    // Always executed (success or failure)
    onComplete: () => {
      closeModal();
      setShowResultTable(true);
    }
  });

  // Opens confirmation modal only if IMEIs exist
  const showUnclaimModal = () => hardwareIds && setIsModalOpen(true);

  // Closes confirmation modal
  const closeModal = () => setIsModalOpen(false);

  /**
   * Parses uploaded CSV file and extracts IMEIs.
   * Stages each IMEI with default status/message before processing.
   */
  const parseInputFile = file => {
    const reader = new FileReader();

    reader.onabort = () =>
      addErrorMessage({ details: 'file reading was aborted' });

    reader.onerror = () => addErrorMessage({ details: 'file reading failed' });

    reader.onload = () => {
      const binaryStr = reader.result;
      const csv = arrayBufferToString(binaryStr);

      // Convert each line into a staged IMEI object
      const hardwareIds = csv
        .split('\n')
        .map(unparsedHardwareId => {
          const hardwareId = unparsedHardwareId.trim().replace(/\D/g, ''); // Strip non-numeric chars

          if (hardwareId) {
            return {
              hardwareId,
              status: 'Staged',
              message: 'Click unclaim to start the process'
            };
          }
          return null;
        })
        .filter(Boolean);

      setHardwareIds(hardwareIds);
    };

    reader.readAsArrayBuffer(file);
  };

  /**
   * Resets component to initial state.
   */
  const handleReset = () => {
    resetFetchState();
    setHardwareIds();
    setShowResultTable(false);
  };

  /**
   * Exports processed unclaim results to CSV file.
   */
  const handleExport = () => {
    const headers = ['hardwareId', 'status', 'message', 'product'];

    const csv = `${headers.join(',')}\n${hardwareIds
      .map(item => headers.map(header => item[header]).join(','))
      .join('\n')}`;

    const csvBlob = new Blob([csv], {
      type: 'text/csv;charset=utf-8;'
    });

    FileSaver.saveAs(csvBlob, `BulkUnclaim-${getCurrentDateTimeString()}.csv`);

    // Track export event
    trackCustomEvents('Download');
  };

  return (
    <div className="BulkUnclaim">
      {/* CSV upload and primary action */}
      <FileUpload
        title="Please load the IMEIs in csv file (separated in rows)"
        inputPlaceholderText=" Click to upload csv file"
        onReset={handleReset}
        onUpload={parseInputFile}
        actionBtnClick={showUnclaimModal}
        actionBtnName="Unclaim All"
        isUnclaim
      />

      {/* Render staged or result table once IMEIs are available */}
      {hardwareIds && (
        <DynamicList
          headerNameListItemMap={
            showResultTable
              ? resultHeaderNameListItemMap
              : headerNameListItemMap
          }
          listItems={hardwareIds}
        />
      )}

      <div className="Unclaim-btn-container">
        {/* Confirmation modal for bulk unclaim */}
        <ConfirmationModal
          isOpen={isModalOpen}
          onAcceptBtnClicked={actionTrigger}
          onDenyBtnClicked={closeModal}
          buttonsDisabled={fetchState === FETCH_STATE.LOADING}
          title="Are you sure you want to unclaim these devices?"
          message={`You are about to unclaim ${hardwareIds?.length} ${pluralize(
            { string: 'device', count: hardwareIds?.length }
          )}. This operation is not reversible.`}
          acceptBtnTitle="Unclaim"
          denyBtnTitle="Cancel"
        />

        {/* Download button shown only after successful completion */}
        {fetchState === FETCH_STATE.COMPLETE && (
          <TmoButton
            className="Unclaim-reset-btn Unclaim-reset-btn:hover"
            onClick={handleExport}
          >
            Download
          </TmoButton>
        )}

        {/* Raw error display (fallback) */}
        {error && `There was an error ${error}}`}
      </div>
    </div>
  );
};

export default Unclaim;
